import { useEffect } from "react"
import { Card } from "ui/Card"
import { Title, Text } from "ui/Typography"
import { useTranslation, Trans } from "react-i18next"
import { Button, Flex, Alert } from "antd"
import { spacings } from "themes/variables"
import { useDispatch, useSelector } from "react-redux"
import { connectPayPal, disconnectPayPal } from "store/payment-provider"
import PaypalStatus from "./PaypalStatus"
import { getUser, isPaypalConnectedSelector, paypalAccountDataSelector } from "store/user"
import { paypalLogoUrl } from "helpers/constant"
import { useNewConfirmModal } from "modules/modals/hooks/useNewConfirmModal"

const PaypalAccountCard = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { showConfirm, modal } = useNewConfirmModal()
  const isPaypalConnectLoading = useSelector((state) => state.paymentProvider?.connectPayPal?.loading)
  const isPaypalDisconnectLoading = useSelector((state) => state.paymentProvider?.disconnectPayPal?.loading)
  const isPaypalConnected = useSelector(isPaypalConnectedSelector)
  const paypalAccountData = useSelector(paypalAccountDataSelector)

  useEffect(() => {
    dispatch(getUser())
  }, [dispatch])

  const handleConnectPayPal = async () => {
    const response = await dispatch(connectPayPal())
    if (response?.action_url) {
      window.location.replace(response.action_url)
    }
  }

  const handleDisconnectPayPal = async () => {
    await dispatch(disconnectPayPal())
    dispatch(getUser())
  }

  const handleDisconnectWithConfirmation = () => {
    showConfirm(
      {
        message: t("pro.settings.paypal.disconnect_confirmation"),
        confirmLabel: t("global.yes"),
        cancelLabel: t("global.cancel")
      },
      handleDisconnectPayPal
    )
  }
  
  const showPaymentsNotReceivableError = isPaypalConnected && paypalAccountData.payments_receivable === false
  const showEmailNotConfirmedError = isPaypalConnected && paypalAccountData.email_confirmed === false

  return (
    <>
      <Card className="mt-m">
        <Flex vertical gap={spacings.M}>
          <Flex align="center" gap={spacings.S}>
            <img src={paypalLogoUrl} alt="Paypal icon" width={50} />
            <Flex vertical gap={spacings.XS} flex={1}>
              <Title level={4}>{t("pro.settings.paypal.paypal_account")}</Title>
              <Text>{t("pro.settings.paypal.card_description")}</Text>
            </Flex>
            <PaypalStatus isPaypalConnected={isPaypalConnected} />
            <Button
              size="large"
              type="primary"
              onClick={isPaypalConnected ? handleDisconnectWithConfirmation : handleConnectPayPal}
              loading={isPaypalConnectLoading || isPaypalDisconnectLoading}
              disabled={isPaypalConnectLoading || isPaypalDisconnectLoading}
            >
              {isPaypalConnected ? t("global.disconnect") : t("global.connect")}
            </Button>
          </Flex>

          {showPaymentsNotReceivableError && (
            <Alert
              message={
                <Trans
                  i18nKey="pro.settings.paypal.payments_not_receivable"
                  components={[
                    <a
                      key="paypal-link"
                      href="https://www.paypal.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Visit PayPal website for support"
                    />
                  ]}
                />
              }
              type="error"
              showIcon
            />
          )}

          {showEmailNotConfirmedError && (
            <Alert
              message={
                <Trans
                  i18nKey="pro.settings.paypal.email_not_confirmed"
                  components={[
                    <a
                      key="paypal-settings-link"
                      href="https://www.paypal.com/businessprofile/settings"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Visit PayPal business profile settings"
                    />
                  ]}
                />
              }
              type="warning"
              showIcon
            />
          )}
        </Flex>
      </Card>
      {modal}
    </>
  )
}

export default PaypalAccountCard
